/**
 * MeshBuilder - 3D Mesh Generation from 2D Sketches
 *
 * Converts 2D SketchPaths into 3D Three.js meshes using:
 * - Earcut for triangulation
 * - Extrusion for depth
 */

import * as THREE from 'three';
import earcut from 'earcut';
import { subtractCutouts, ensureWinding } from './PolygonOps.js';

export class MeshBuilder {
    /**
     * Create mesh builder
     */
    constructor() {
        this.defaultDepth = 0.1; // Default extrusion depth
        this.defaultColor = 0xffffff; // White color for all meshes
    }

    /**
     * Generate vertices from SketchPath
     * @param {SketchPath} path - Closed path
     * @param {number} [detail=0.1] - Detail level for Bezier curves (smaller = more detail)
     * @returns {Array} Array of {x, y} vertices
     */
    getVertices(path, detail = 0.1) {
        if (!path.getClosed()) {
            console.warn('Path is not closed, closing it');
            path.setClosed(true);
        }

        const vertices = [];
        const points = path.points;

        if (points.length < 3) {
            console.error('Path must have at least 3 points');
            return vertices;
        }

        // Extract vertices, handling Bezier curves
        for (let i = 0; i < points.length; i++) {
            const curP = points[i];
            const nextP = points[(i + 1) % points.length];

            // Add current point
            vertices.push({ x: curP.x, y: curP.y });

            // If there's a Bezier curve to the next point, subdivide it
            if (curP.containsBezier() || nextP.containsBezier()) {
                const cp1 = curP.getControlPoint2();
                const cp2 = nextP.getControlPoint1();

                // Subdivide Bezier curve
                for (let t = detail; t < 1.0; t += detail) {
                    const pos = this.bezierPoint(
                        curP.x, curP.y,
                        cp1.x, cp1.y,
                        cp2.x, cp2.y,
                        nextP.x, nextP.y,
                        t
                    );
                    vertices.push({ x: pos.x, y: pos.y });
                }
            }
        }

        return vertices;
    }

    /**
     * Evaluate cubic Bezier curve
     * @param {number} x0, y0 - Start point
     * @param {number} x1, y1 - Control point 1
     * @param {number} x2, y2 - Control point 2
     * @param {number} x3, y3 - End point
     * @param {number} t - Parameter (0-1)
     * @returns {Object} {x, y} point on curve
     */
    bezierPoint(x0, y0, x1, y1, x2, y2, x3, y3, t) {
        const t1 = 1 - t;
        return {
            x: t1 * t1 * t1 * x0 + 3 * t1 * t1 * t * x1 + 3 * t1 * t * t * x2 + t * t * t * x3,
            y: t1 * t1 * t1 * y0 + 3 * t1 * t1 * t * y1 + 3 * t1 * t * t * y2 + t * t * t * y3
        };
    }

    /**
     * Triangulate 2D polygon using earcut
     * @param {Array} vertices - Array of {x, y} vertices
     * @returns {Array} Triangle indices
     */
    triangulate(vertices) {
        if (vertices.length < 3) {
            return [];
        }

        // Flatten vertices for earcut [x0, y0, x1, y1, ...]
        const coords = [];
        for (let i = 0; i < vertices.length; i++) {
            coords.push(vertices[i].x, vertices[i].y);
        }

        // Triangulate
        try {
            return earcut(coords);
        } catch (error) {
            console.error('Triangulation failed:', error);
            return [];
        }
    }

    /**
     * Create extruded 3D mesh from 2D path
     * @param {SketchPath} path - Closed path
     * @param {Object} options - Extrusion options
     * @returns {THREE.Mesh} Three.js mesh
     */
    createExtrudedMesh(path, options = {}) {
        const {
            depth = this.defaultDepth,
            color = this.defaultColor,
            wireframe = false,
            centerY = true, // Center mesh on Y axis
            cutouts = null  // Array of rings (Array<{x,y}>) to boolean-subtract (slots)
        } = options;

        // Get 2D vertices
        const vertices2D = this.getVertices(path);

        if (vertices2D.length < 3) {
            console.error('Not enough vertices to create mesh');
            return null;
        }

        // Holes carried by the path itself (e.g. a closed brush stroke
        // whose outline is an annulus)
        const pathHoles = (path.holes || [])
            .map(hole => hole.map(p => ({ x: p.x, y: p.y })))
            .filter(hole => hole.length >= 3);

        // Subtract slot cutouts (waffle joints) - may produce holes/notches
        let polygons;
        if (cutouts && cutouts.length > 0) {
            polygons = subtractCutouts(vertices2D, cutouts, pathHoles);
        } else {
            polygons = [{ outer: vertices2D, holes: pathHoles }];
        }

        const mesh = this.createMeshFromPolygons(polygons, { depth, color, wireframe });
        if (!mesh) return null;

        // Center on Y axis if requested
        if (centerY) {
            mesh.geometry.center();
        }

        return mesh;
    }

    /**
     * Create an extruded mesh directly from pre-sampled polygons-with-holes
     * (e.g. the union of every stroke on a plane with slot cutouts already
     * subtracted). Each polygon is { outer: Array<{x,y}>, holes: Array<ring> }.
     *
     * @param {Array<{outer, holes}>} polygons - Polygons in mesh-local units
     * @param {Object} options - { depth, color, wireframe }
     * @returns {THREE.Mesh|null} Three.js mesh
     */
    createMeshFromPolygons(polygons, options = {}) {
        const {
            depth = this.defaultDepth,
            color = this.defaultColor,
            wireframe = false
        } = options;

        const geometry = this.buildExtrudedGeometry(polygons, depth);
        if (!geometry) {
            console.error('Triangulation produced no triangles');
            return null;
        }

        // Create material
        const material = new THREE.MeshStandardMaterial({
            color: color,
            side: THREE.DoubleSide,
            wireframe: wireframe,
            flatShading: false,
            roughness: 0.7,
            metalness: 0.1
        });

        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        console.log('Created mesh:', geometry.getAttribute('position').count, 'vertices,', geometry.getIndex().count / 3, 'triangles');

        return mesh;
    }

    /**
     * Build extruded BufferGeometry from polygons with holes.
     * Each polygon is { outer: Array<{x,y}>, holes: Array<Array<{x,y}>> }.
     * Front/back faces are triangulated with earcut (hole support);
     * side walls are emitted for every ring including holes.
     *
     * @param {Array<{outer, holes}>} polygons - Polygons in mesh-local units
     * @param {number} depth - Extrusion depth
     * @returns {THREE.BufferGeometry|null} Geometry or null if empty
     */
    buildExtrudedGeometry(polygons, depth) {
        const positions = [];
        const allIndices = [];
        const halfDepth = depth / 2;

        for (const polygon of polygons) {
            // Normalize winding: outer CCW, holes CW - keeps earcut output and
            // side-wall orientation consistent
            const outer = ensureWinding(polygon.outer, true);
            const holes = (polygon.holes || []).map(h => ensureWinding(h, false));
            const rings = [outer, ...holes];

            // Flatten rings for earcut, tracking ring start offsets
            const coords = [];
            const holeIndices = [];
            const ringOffsets = [];
            let vertCount = 0;

            for (let r = 0; r < rings.length; r++) {
                if (r > 0) holeIndices.push(vertCount);
                ringOffsets.push({ start: vertCount, length: rings[r].length });
                for (const v of rings[r]) {
                    coords.push(v.x, v.y);
                    vertCount++;
                }
            }

            let indices;
            try {
                indices = earcut(coords, holeIndices.length > 0 ? holeIndices : null);
            } catch (error) {
                console.error('Triangulation failed for polygon:', error);
                continue;
            }
            if (!indices || indices.length === 0) continue;

            const base = positions.length / 3;
            const backOffset = vertCount;

            // Front face vertices (z = +halfDepth), then back face (z = -halfDepth)
            for (let i = 0; i < coords.length; i += 2) {
                positions.push(coords[i], coords[i + 1], halfDepth);
            }
            for (let i = 0; i < coords.length; i += 2) {
                positions.push(coords[i], coords[i + 1], -halfDepth);
            }

            // Front face triangles (CCW - outer ring is CCW)
            for (let i = 0; i < indices.length; i += 3) {
                allIndices.push(base + indices[i], base + indices[i + 1], base + indices[i + 2]);
            }

            // Back face triangles (reversed)
            for (let i = 0; i < indices.length; i += 3) {
                allIndices.push(
                    base + backOffset + indices[i + 2],
                    base + backOffset + indices[i + 1],
                    base + backOffset + indices[i]
                );
            }

            // Side walls for every ring (outer boundary + hole walls)
            for (const ring of ringOffsets) {
                for (let i = 0; i < ring.length; i++) {
                    const a = base + ring.start + i;
                    const b = base + ring.start + ((i + 1) % ring.length);
                    allIndices.push(a, b, a + backOffset);
                    allIndices.push(b, b + backOffset, a + backOffset);
                }
            }
        }

        if (allIndices.length === 0) {
            return null;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setIndex(allIndices);
        geometry.computeVertexNormals();

        return geometry;
    }

    /**
     * Create flat mesh (no extrusion) from 2D path
     * @param {SketchPath} path - Closed path
     * @param {Object} options - Options
     * @returns {THREE.Mesh} Three.js mesh
     */
    createFlatMesh(path, options = {}) {
        const {
            color = this.defaultColor,
            wireframe = false
        } = options;

        // Get 2D vertices
        const vertices2D = this.getVertices(path);

        if (vertices2D.length < 3) {
            console.error('Not enough vertices to create mesh');
            return null;
        }

        // Triangulate
        const indices = this.triangulate(vertices2D);

        if (indices.length === 0) {
            console.error('Triangulation produced no triangles');
            return null;
        }

        // Create geometry
        const geometry = new THREE.BufferGeometry();

        // Create positions array
        const positions = [];
        for (let i = 0; i < vertices2D.length; i++) {
            const v = vertices2D[i];
            positions.push(v.x, v.y, 0);
        }

        // Set geometry attributes
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        geometry.center();

        // Create material
        const material = new THREE.MeshStandardMaterial({
            color: color,
            side: THREE.DoubleSide,
            wireframe: wireframe
        });

        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);

        return mesh;
    }
}
